<template>
  <div
    :class="template"
    class="card-template"
  >
    <CardsSidebar
      ref="scrollContainer"
      class="card-template__sidebar"
      :sidebar-cards="sidebarCards"
      @click-sidebar-item="clickSidebaritem"
    />
    <div class="card-template__main">
      <div class="card-template__substrate">
        <div
          :style="cardBg"
          class="card-template__wrapp"
        >
          <div
            v-if="isShowArticleCard && article"
            class="card-template__header"
          >
            <ArticleCard :article="article" />
          </div>
          <div
            class="card-template__content card-template__content--article-content default-article__analytics"
          >
            <div
              :class="{
                'default-article__content-wrapper--page': !isShowArticleCard,
              }"
              class="card-template__content-wrapper"
            >
              <h1
                v-if="!isShowArticleCard && article"
                class="card-template__headline"
                v-html="article.title"
              />
              <div
                v-if="article.parsed_content.length"
                class="chunks-wrapp"
              >
                <div
                  v-for="(chunk, index) in article.parsed_content"
                  :key="index"
                  ref="chunk"
                  class="chunk"
                >
                  <ContentChunk
                    :index-chunk="index"
                    :chunk="chunk"
                    :is-last-chunk="isLastChunk(index)"
                  />
                </div>
              </div>
            </div>
            <ArticleFooter
              v-if="isShowArticleCard"
              class="card-template__footer"
            />
          </div>
        </div>
      </div>
      <div
        v-if="isShowBottomPart"
        class="card-template__content card-template__content--bottom"
      >
        <div class="card-template__content-wrapper">
          <WidgetsArea area="above-comments" />

          <!-- комментарии -->
          <CommentsContainer
            v-if="showComments"
            :id="article.id"
            class="card-template__comments comments"
          />

          <WidgetsArea area="below-comments" />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  // @ts-expect-error throttle-debounce types mismatch with current TS config
  import { throttle } from 'throttle-debounce';
  import CardsSidebar from './CardsSidebar.vue';
  import ContentChunk from './ContentChunk.vue';
  import ArticleCard from '~/components/ArticleCard.vue';
  import ArticleFooter from '~/components/ArticleFooter.vue';
  import WidgetsArea from '~/components/WidgetsArea/WidgetsArea.vue';
  import CommentsContainer from '~/components/CommentsContainer.vue';

  import { useSingleArticleStore } from '~/store/singleArticle';
  import type { IFullArticle } from '~/types/common';

  type TSidebarCard = {
    title: string;
  };

  export default defineNuxtComponent({
    name: 'CardsTemplate',

    components: {
      ArticleCard,
      ArticleFooter,
      WidgetsArea,
      ContentChunk,
      CardsSidebar,
      CommentsContainer,
    },

    provide() {
      return {
        isEndArticleReadAlso: (): boolean => {
          return Boolean(this.article?.parsed_content?.at(-1)?.type === 'burninghut__read-also');
        },

        rubricColor: (): string => {
          return this.article?.categories[0].color || '#EBEBEB';
        },

        activeCardIndex: (): number => {
          return this.activeCardIndex;
        },

        isPartner: (): boolean => {
          return !!this.article?.is_advertising;
        },
      };
    },

    data() {
      return {
        activeCardIndex: -1,
        throttledScroll: throttle(this.scroll, 100),
      };
    },

    computed: {
      isShowBottomPart(): boolean {
        return !!this.article && !this.isPage;
      },

      cardBg(): Record<string, string> {
        const isPartner = this.article?.is_advertising;
        if (isPartner) {
          return {
            '--cards-bg': '#EBEBEB',
          };
        }
        return {
          '--cards-bg': this.article?.categories[0].color || '#EBEBEB',
        };
      },

      article() {
        const article = useSingleArticleStore().article;

        if (!article) {
          throw createError({ status: 500, message: `Article hasn't been provided from pinia` });
        }

        return article as IFullArticle;
      },

      isShowArticleCard(): boolean {
        return this.article?.type === 'post';
      },

      subtitle() {
        if (this.article?.parsed_content) {
          return this.article.parsed_content[0];
        }
        return null;
      },

      showComments(): boolean | undefined {
        return this.article?.comments_enabled && !this.article?.is_promo;
      },

      isPage(): boolean {
        return this.article?.type === 'page';
      },

      template(): Record<string, boolean | undefined> {
        const className = 'card-template';
        return {
          [`${className}--page`]: this.isPage,
        };
      },

      sidebarCards(): Array<TSidebarCard> | null {
        if (this.article) {
          return this.article.parsed_content.map((chunk) => {
            return { title: chunk.attributes.title };
          });
        }
        return null;
      },
    },

    watch: {
      activeCardIndex(index) {
        if (index !== -1) {
          this.scrollToMenuItem();
        }
      },
    },

    mounted() {
      if (window.innerWidth >= 1024) {
        window.addEventListener('scroll', this.throttledScroll);
      }
    },

    beforeUnmount() {
      window.removeEventListener('scroll', this.throttledScroll);
    },

    methods: {
      isLastChunk(index: number) {
        if (this.article) {
          return index === this.article.parsed_content.length - 1;
        }
        return false;
      },

      clickSidebaritem(index: number) {
        // @ts-expect-error refs array typing is not inferred in Vue 2 options API
        const activeChunk = this.$refs.chunk[index];

        // Учитываем высоту хедера при доскролле
        const y = activeChunk.getBoundingClientRect().top + window.pageYOffset - 50;

        window.scrollTo({ top: y, behavior: 'smooth' });
      },

      onScroll(): void {
        const cards = document.querySelectorAll('.chunk');

        const cardsArray = Array.from(cards);
        this.activeCardIndex = cardsArray.findIndex(this.isActiveElem);
      },

      isActiveElem(elem: Element): boolean {
        const { top, bottom } = elem.getBoundingClientRect();
        const TOP_OFFSET = 50 + 24; // высота header + отступ от меню до header
        return top < window.innerHeight + 50 && bottom > TOP_OFFSET;
      },

      // отвечает за автоматический скролл сайдбара, при скролле основного контента
      scrollToMenuItem() {
        if (window.innerWidth >= 1024) {
          const scrollContainer = (this.$refs.scrollContainer as unknown)?.$el as HTMLElement;
          const menuItem = document.querySelectorAll('.cards-sidebar-item')[this.activeCardIndex];
          const listHeight = scrollContainer.offsetHeight;
          const coordsItem = menuItem.getBoundingClientRect();
          const coordsList = scrollContainer.getBoundingClientRect();
          const coordsItemByParent = coordsItem.top - coordsList.top;

          let isVisibleMenuItem;

          if (coordsItemByParent < 0 || coordsItemByParent + menuItem.clientHeight > listHeight) {
            isVisibleMenuItem = false;
          } else {
            isVisibleMenuItem = true;
          }

          if (!isVisibleMenuItem) {
            scrollContainer.scrollTop =
              coordsItemByParent > 0
                ? coordsItemByParent +
                  menuItem.clientHeight -
                  scrollContainer.offsetHeight +
                  scrollContainer.scrollTop +
                  10
                : scrollContainer.scrollTop + coordsItemByParent;
          }
        }
      },
    },
  });
</script>

<style lang="scss" scoped>
  @use '@devhacker/shared/styles/scrollbar.scss' as scrollbar;
  @use '~/scss/article' as *;

  .card-template {
    @include container;
    padding-bottom: $spacing-8;
    background: #fff;
    @include tablet {
      padding-bottom: $spacing-10;
      margin-top: 24px !important;
    }

    @include big-tablet {
      padding-left: $spacing-8;
      padding-right: $spacing-8;
      max-width: unset;
      display: grid;
      grid-template-columns: 296px auto;
      grid-column-gap: 6px;
    }

    @include desktop {
      padding-left: $spacing-9;
      padding-right: $spacing-9;
      max-width: 1280px;
      grid-column-gap: 26px;
    }

    @include header-margin;

    &__header {
      :deep() {
        .article-card--cards {
          @include tablet {
            padding-left: $spacing-16;
            padding-right: $spacing-16;
          }
          @include big-tablet {
            padding-left: $spacing-8;
            padding-right: $spacing-8;
          }
          @include desktop {
            padding-left: $spacing-19;
            padding-right: $spacing-19;
          }
        }
      }
    }

    &__substrate {
      background: linear-gradient(180deg, hsla(0, 0%, 0%, 1) 30%, hsla(0, 0%, 100%, 1) 100%);
      padding-top: 4px;
      @include tablet {
        padding-top: 0;
        background: #fff;
      }
    }

    &__wrapp {
      border-radius: 32px 32px 34px 34px;
      background: var(--cards-bg);
      @include big-tablet {
        border-radius: 48px 48px 40px 40px;
      }
    }

    &--page {
      padding-top: $spacing-6;
      @include big-tablet {
        padding-top: $spacing-8;
      }
      @include desktop {
        padding-top: $spacing-9;
      }
    }

    &__sidebar {
      position: sticky;
      top: $spacing-15;
      overflow: auto;
      scrollbar-gutter: stable;
      height: calc(100vh - 65px);
      display: none;
      @include scrollbar.customScrollbar;
      @include big-tablet {
        display: block;
      }
    }

    &__main {
      font-family: $text-font--1;
      max-width: 100%;

      @include tablet {
        max-width: 720px;
      }

      @include big-tablet {
        max-width: 656px;
      }

      @include desktop {
        max-width: 884px;
      }
    }
    &__content {
      padding: 0 2px 2px;
      @include tablet {
        padding: 0 8px 8px;
      }
      &--bottom {
        padding: 0;
        margin-top: $spacing-8;
        @include tablet {
          margin-top: $spacing-10;
        }

        .widgets-area {
          &:first-child {
            margin-top: $spacing-0;
          }
          @include tablet {
            padding: $spacing-0;
            margin: $spacing-0 auto;
          }

          @include big-tablet {
            width: 592px;
            margin: $spacing-6 auto;
          }

          @include desktop {
            width: 732px;
          }
        }
      }
    }

    &__footer {
      background: #fff;
      border-top: 0;
      padding: $spacing-6 14px $spacing-4;
      border-radius: 0 0 34px 34px;

      @include tablet {
        border-radius: 0 0 32px 32px;
        border: 0px;
        padding: $spacing-6 56px;
      }
      @include big-tablet {
        padding: $spacing-6 24px $spacing-6;
      }
      @include desktop {
        padding: $spacing-6 $spacing-17 $spacing-6;
      }
    }

    &__content-wrapper:not(.card-template__content-wrapper--page) {
      :deep() > {
        @include lead-styles;
      }
    }

    .card-template__content--bottom > .card-template__content-wrapper {
      padding-left: $spacing-4;
      padding-right: $spacing-4;
      @include tablet {
        padding: $spacing-0 $spacing-16;
      }
      @include big-tablet {
        padding: $spacing-0;
      }
    }

    .card-template__content-wrapper {
      @include article-picture-styles;
      @include tablet {
        padding: $spacing-0;
      }

      :deep() {
        .widgets-area {
          .accent-cards {
            .accent-cards__wrapper--2,
            .accent-cards__wrapper--4 {
              .accent-card--small {
                @include big-tablet {
                  max-height: 348px;
                  min-height: 348px;
                }
                @include desktop {
                  min-height: 400px;
                  max-height: 440px;
                }
              }
            }
          }
        }
      }
    }

    &__headline {
      margin-top: $spacing-2;
      font-family: $headline-font--1;
      font-weight: 400;
      @include headline-normal-text;
      margin-bottom: $spacing-3;

      @include tablet {
        margin-top: $spacing-4;
        @include headline-big-text;
      }
    }
  }

  .comments {
    padding: 0;
    margin: 32px auto 0;
    @include tablet {
      margin: 40px auto 0;
    }
    &:not(:last-child) {
      margin-bottom: $spacing-8;
      @include tablet {
        margin-bottom: $spacing-10;
      }
    }
    @include big-tablet {
      width: 592px;
    }
    @include desktop {
      width: 732px;
    }

    :deep() .lh-talker-app {
      margin-top: 0;
      margin-bottom: 0;

      .form {
        > *:first-child {
          border: 2px solid $stroke-color--1;

          @include hover {
            border-color: #699bdc;
          }
        }
      }
    }
  }

  .chunks-wrapp {
    :deep() {
      .chunk:last-child {
        padding: $spacing-0;
        .content {
          border-radius: 32px 32px 0 0;
          margin: $spacing-0;
          padding: $spacing-8 14px $spacing-6;
          @include tablet {
            padding: $spacing-10 56px $spacing-6;
          }
          @include big-tablet {
            padding: $spacing-10 24px $spacing-6;
          }
          @include desktop {
            padding: $spacing-10 $spacing-17 $spacing-6;
          }
        }
      }
    }
  }
</style>
